import { useQuery } from '@tanstack/react-query';

import type {
  QueryFunctionContext,
  QueryObserverResult,
  QueryFunction,
} from '@tanstack/react-query';

interface ASNQuery {
  data: {
    asn: {
      organization: {
        orgName: string;
      } | null;
    };
  };
}

interface ASNResolverResponse {
  asn: number;
  name: string | null;
  holder: string | null;
  found: boolean;
  source: string;
  updated_at?: number;
  fresh?: boolean;
}

const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

const query: QueryFunction<ASNQuery, string[]> = async (ctx: QueryFunctionContext) => {
  const [asn] = ctx.queryKey as string[];

  const res = await fetch(`/asn-resolver/asn/${encodeURIComponent(asn)}`, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!res.ok) {
    throw new Error(`ASN resolver returned HTTP ${res.status}`);
  }

  const result = (await res.json()) as ASNResolverResponse;

  return {
    data: {
      asn: {
        organization:
          result.found && result.name
            ? {
                orgName: result.name,
              }
            : null,
      },
    },
  };
};

/**
 * Query the local NetNite ASN resolver.
 *
 * The resolver uses a local SQLite cache with a 30-day TTL
 * and RIPEstat as the upstream source when the cache is missing
 * or expired.
 */
export function useASNDetail(asn: string): QueryObserverResult<ASNQuery> {
  return useQuery<ASNQuery, unknown, ASNQuery, string[]>({
    queryKey: [asn],
    queryFn: query,
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchOnMount: true,
    staleTime: THIRTY_DAYS,
    cacheTime: THIRTY_DAYS,
  });
}
